import React, { useContext } from "react";
import { localOrdersSnacksDatePath, snacksPath, userOrdersPathDate } from "../../functions/src/admin/paths";
import { KEY_MAX_QUANTITY_LOCAL, MAX_SNACK_ITEMS } from "../../functions/src/api/constants";
import { Snack } from "../../functions/src/types";
import { auth, currentCatering, DataSnapshot } from "../api/firebase";
import { EMPTY_FILTER, useDatabase, useSnapshotState } from "../api/firebaseHooks";
import { getLangValue, langContext } from "../api/Lang";
import { formatPriceEuro } from "../api/priceFormat";
import { useStrings } from "../common-strings";
import { useOrderStrings } from "./order-strings";

const ITEMS_WARNING = 10;

export default function SnacksPopUp({ date, snacksList, typesList, recipient, schoolBreak, snapshot }: SnacksPopUpProps) {

    const [lang] = useContext(langContext);
    const filteredSnapshot = snapshot.filter(sSnack => sSnack.child('schoolBreak').val() === schoolBreak && sSnack.child('recipient').val() === recipient);
    const localSnacks = useDatabase(snacksPath(currentCatering), EMPTY_FILTER, false, false);

    const snacksMaxLocal = useDatabase(localOrdersSnacksDatePath(date));

    // Sort snacks list according to order variable in local Db
    snacksList = snacksList.sort((a, b) => {
        const snackA: DataSnapshot<Snack> = localSnacks?.child(a.key);
        const snackB: DataSnapshot<Snack> = localSnacks?.child(b.key);
        let orderA = a.val().order;
        let orderB = b.val().order;
        if (snackA?.child('order').exists())
            orderA = snackA.val().order;
        if (snackB?.child('order').exists())
            orderB = snackB.val().order;
        return orderA - orderB;
    });

    return <div>
        {typesList.filter(s_type => s_type.child('order').val() > 0 && snacksList?.some(s_snack => s_snack.child('snackType').val() === s_type.key))
            .map((sType, index) =>
                <div id={index.toString()} key={sType.key} >
                    <SnackCategory heading={sType.child(lang).val()} subTitle={sType.child('subTitle')?.child(lang).val()}></SnackCategory>
                    {

                        snacksList.filter(sSnack => {
                            const s_localSnack = localSnacks?.child(sSnack.key);

                            // order field: local override with fallback to main
                            const order = s_localSnack?.child('order')?.val() ?? sSnack.child('order')?.val() ?? -1;

                            return sSnack.child('snackType').val() === sType.key && order >= 0
                        })
                            .map(s_line => {
                                const s_localSnack = localSnacks?.child(s_line.key);

                                // Only use local quantity limits
                                const maxQuantityLocal = s_localSnack?.child(KEY_MAX_QUANTITY_LOCAL)?.val() || 1000000;
                                const snacksRemaining = maxQuantityLocal - (snacksMaxLocal?.child(s_line.key)?.val() ?? 0);

                                return <SnackPopUpLine key={s_line.key}
                                    date={date}
                                    snackKey={s_line.key}
                                    snack={s_line.val()}
                                    recipient={recipient}
                                    schoolBreak={schoolBreak}
                                    qSnapshot={filteredSnapshot.filter(sSnack => sSnack.child('snackKey').val() === s_line.key)[0]}
                                    snacksRemaining={snacksRemaining}
                                />
                            }
                            )}
                </div>)
        }
    </div>
}

type SnacksPopUpProps = {
    date: string;
    snacksList: DataSnapshot<Snack>[];
    typesList: DataSnapshot[];
    recipient: string;
    schoolBreak: string;
    snapshot: DataSnapshot[]; // Snacks of order per recipient and time
}

function SnackCategory({ heading, subTitle }: { heading: string, subTitle: string }) {

    return <div style={{ marginBottom: '32px' }}>
        <div className='popup-heading'>{heading}</div>
        {/* <div className='menu-description' style={{color: 'darkred'}}>{subTitle}</div> */}
    </div>
}

function SnackPopUpLine({ date, snackKey, snack, recipient, schoolBreak, qSnapshot, snacksRemaining }: SnackPopUpLineProps) {

    const [lang] = useContext(langContext);
    const commonStrings = useStrings();
    const orderStrings = useOrderStrings();

    const [quantity, setQuantity] = useSnapshotState<number>(qSnapshot, 'quantity');
    const { uid } = auth.currentUser;

    const sOrder = useDatabase(date && userOrdersPathDate(uid, date) || null);

    const onAddSnack = () => {
        sOrder.ref.child('snacks').push({
            quantity: 1,
            snackKey,
            recipient,
            schoolBreak
        });
    }

    const onSubtract = () => {
        if (quantity > 1)
            setQuantity(quantity - 1);
        else {
            qSnapshot.ref.remove();
        }
    }

    const onAdd = () => {

        if (!quantity)
            onAddSnack();
        else {
            if (quantity < MAX_SNACK_ITEMS)
                setQuantity(quantity + 1);
        }
    }

    const isDisabled = snacksRemaining <= 0;//(snack.snackType === "pies") && (schoolBreak === '2' || schoolBreak === '3');
    const fewItemsLeft = snacksRemaining < ITEMS_WARNING;
    const snacksRemainingTxt = isDisabled ? commonStrings.soldOut : fewItemsLeft ? orderStrings.snacksPopupFewItemsLeft(snacksRemaining) : '';

    return <div style={{ opacity: isDisabled ? '0.5' : '1' }}>
        <div className={`popup-title-line ${quantity > 0 ? 'selected' : ''}`} style={{ marginTop: '16px' }}>
            <span className='popup-title'>{getLangValue(snack.name, lang)}</span>
        </div>
        <div className='popup-title popup-descr'>{getLangValue(snack.description, lang)}</div>
        <div style={{ marginTop: '8px' }}>
            {!!snack.bio && <img className="meal-attr-icons" src="/img-assets/bio.svg" title={commonStrings.bio} />}
            {!!snack.vegan && <img className="meal-attr-icons" src="/img-assets/vegan.svg" title={commonStrings.vegetarian} />}
            {!!snack.glutenFree && <img className="meal-attr-icons" src="/img-assets/gluten-free.svg" title={commonStrings.glutenFree} />}
            {!!snack.frozen && <img className="meal-attr-icons" src="/img-assets/frozen.svg" title={commonStrings.frozen} />}
        </div>
        <div className="order-line-row " style={{ marginTop: '16px' }}>
            <div className="list-price">{snack.price != null ? formatPriceEuro(Number(snack.price), lang) : formatPriceEuro(0, lang)}</div>
            <div className={`popup-title-line ${quantity > 0 ? 'selected' : ''}`}>
                {fewItemsLeft && <span className='items-left'>{snacksRemainingTxt}</span>}
                <button className='add-items-button' onClick={onSubtract} disabled={!quantity || quantity <= 0}>-</button>
                <span className='num-items'>{quantity || 0}</span>
                <button className='add-items-button' onClick={onAdd} disabled={isDisabled || quantity >= MAX_SNACK_ITEMS || quantity >= snacksRemaining}>+</button>
            </div>
        </div>
        <div className="divider-lists"></div>
    </div>
}

type SnackPopUpLineProps = {
    date: string
    snackKey: string
    snack: Snack
    recipient: string
    schoolBreak: string
    qSnapshot: DataSnapshot
    snacksRemaining: number
}